import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, TextField, Button, Alert, Grid,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel, FormHelperText
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

const AddProductInventory = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    generic_product_id: '',
    product_name: '',
    quantity: '',
    expiration_date: '',
    notification_days: 2,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyMethod, setNotifyMethod] = useState(""); // Τώρα δεν έχει default
  const [notifyMethodError, setNotifyMethodError] = useState("");

  useEffect(() => {
    api.genericProducts.list()
      .then(res => {
        // Αν το backend επιστρέφει { success, data: [...] }
        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];

        setProductOptions(data);
      })
      .catch(() => setProductOptions([]));
  }, []);

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    setForm(prev => ({
      ...prev,
      generic_product_id: newValue ? newValue.id : '',
      // Αρκεί να δείχνεις το name που σου στέλνει το backend
      product_name: newValue ? (newValue.name || '') : '',
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setNotifyMethodError('');

    if (!form.generic_product_id || !form.quantity) {
      setError(t("all_fields_required") || "Όλα τα πεδία είναι υποχρεωτικά.");
      return;
    }

    // *** Νέος έλεγχος: Εάν είναι ενεργοποιημένες οι ειδοποιήσεις, ΠΡΕΠΕΙ να έχει επιλεγεί τρόπος ***
    if (notifyEnabled && !notifyMethod) {
      setNotifyMethodError(t("select_notify_method") || "Επιλέξτε τρόπο ειδοποίησης");
      return;
    }

    let quantityToSave = parseFloat(form.quantity);

    // Αν το ID είναι 50 (αυγά), κάνε μετατροπή τεμαχίων σε γραμμάρια
    if (parseInt(form.generic_product_id, 10) === 50) {
      quantityToSave = quantityToSave * 68; // 1 αυγό = 68γρ.
    }

    const payload = {
      generic_product_id: parseInt(form.generic_product_id, 10),
      purchased_quantity: quantityToSave,
      expiration_date: form.expiration_date || null,
      notification_days: form.notification_days ? parseInt(form.notification_days, 10) : 2,
      notify_enabled: notifyEnabled,
      notify_method: notifyMethod,
    };

    try {
      const response = await api.inventory.addOrUpdate(payload);
      if (response.data.success) {
        setSuccess(response.data.message || t("update_inventory_success"));
        setForm({
          generic_product_id: '', product_name: '', quantity: '', expiration_date: '', notification_days: 2
        });
        setSelectedProduct(null);
        setNotifyMethod("");
      } else {
        setError(response.data.error || t("update_inventory_failed"));
      }
    } catch (err) {
      setError(err.response?.data?.error || t("update_inventory_failed"));
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        mt: 4,
        px: { xs: 2, sm: 4 },
        py: 3,
        bgcolor: theme => theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 4
      }}
    >
      <Typography variant="h5" gutterBottom>
        {t("add_product_inventory") || "Add Inventory"}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
      >

        <Autocomplete
          value={selectedProduct}
          onChange={handleProductChange}
          options={productOptions}
          getOptionLabel={(option) =>
            option
              ? (option.name || "")  // το backend δίνει ήδη localized "name"
              : ''
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} label={t("ingredient") || "Υλικό"} required fullWidth />
          )}
        />


        <TextField
          label={t("product_name")}
          name="product_name"
          value={form.product_name}
          fullWidth
          disabled
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("quantity")}
              name="quantity"
              type="number"
              value={form.quantity}
              onChange={handleChange}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("expiration_date")}
              name="expiration_date"
              type="date"
              value={form.expiration_date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  color: isDark ? 'white' : 'black',
                  '& input': {
                    color: isDark ? 'white' : 'black',
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? '#fff' : '#000',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? '#fff' : '#000',
                  },
                },
              }}
              fullWidth
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t("notification_days")}
              name="notification_days"
              type="number"
              value={form.notification_days}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  color: isDark ? 'white' : 'black',
                  '& input': {
                    color: isDark ? 'white' : 'black',
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? '#fff' : '#000',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDark ? '#fff' : '#000',
                  },
                }
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} />
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              checked={notifyEnabled}
              onChange={e => setNotifyEnabled(e.target.checked)}
              name="notifyEnabled"
              color="primary"
            />
          }
          label={t("enable_notification") || "Ενεργοποίηση ειδοποίησης"}
        />

        {notifyEnabled && (
          <FormControl fullWidth error={!!notifyMethodError} required>
            <InputLabel>{t("select_notify_method") || "Επιλέξτε τρόπο ειδοποίησης"}</InputLabel>
            <Select
              value={notifyMethod}
              label={t("select_notify_method") || "Επιλέξτε τρόπο ειδοποίησης"}
              onChange={e => setNotifyMethod(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("select_notify_method") || "Επιλέξτε τρόπο ειδοποίησης"}</em>
              </MenuItem>
              {/* <MenuItem value="push">{t("notification_method_push") || "Ειδοποίηση εφαρμογής"}</MenuItem> */}
              <MenuItem value="email">{t("notification_method_email") || "Email"}</MenuItem>
              {/* <MenuItem value="both">{t("notification_method_both") || "Και τα δύο"}</MenuItem> */}
            </Select>
            {notifyMethodError && <FormHelperText>{notifyMethodError}</FormHelperText>}
          </FormControl>
        )}

        <Button variant="contained" type="submit" sx={{ mt: 2 }}>
          {t("submit") || "Add"}
        </Button>
      </Box>
    </Container>
  );
};

export default AddProductInventory;
